import { useCallback, useRef, useState, type ReactNode } from "react";
import { Button } from "../../ui";

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  danger?: boolean;
}

/**
 * An in-app confirmation (never `window.confirm`, which blocks sync and looks foreign).
 * Render `dialog` once; `confirm()` resolves true or false.
 */
export function useConfirm(): {
  confirm: (o: ConfirmOptions) => Promise<boolean>;
  dialog: ReactNode;
} {
  const [pending, setPending] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);
  const opener = useRef<HTMLElement | null>(null);

  const confirm = useCallback(
    (o: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        resolver.current?.(false);
        resolver.current = resolve;
        opener.current =
          document.activeElement instanceof HTMLElement ? document.activeElement : null;
        setPending(o);
      }),
    [],
  );

  const settle = (ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setPending(null);
    // Focus goes back to whatever asked (e.g. the register field), so Esc and typing still work.
    const back = opener.current;
    opener.current = null;
    if (back?.isConnected) {
      back.focus();
    }
  };

  const dialog = pending && (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink/30 p-4">
      {/* Escape cancels, as in any dialog. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby="confirm-message"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            settle(false);
          }
        }}
        className="flex w-full max-w-[420px] flex-col gap-3 rounded-[16px] border border-line bg-surface p-5 shadow-[0_10px_30px_rgba(51,41,31,.16)]"
      >
        <h2 id="confirm-title" className="m-0 text-[18px] font-extrabold">
          {pending.title}
        </h2>
        <p id="confirm-message" className="m-0 text-[14.5px] text-muted">
          {pending.message}
        </p>
        <div className="flex justify-end gap-2">
          <Button onClick={() => settle(false)}>{pending.cancelLabel ?? "Cancel"}</Button>
          <Button
            variant={pending.danger ? "danger" : "primary"}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- a modal focuses its action
            autoFocus
            onClick={() => settle(true)}
          >
            {pending.confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );

  return { confirm, dialog };
}
